import { useInputControl } from "@conform-to/react";
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from "input-otp";
import React, { useEffect, useId, useRef, useState } from "react";
import { Checkbox, type CheckboxProps } from "~/components/ui/checkbox";
import { Input, type InputProps } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { Icon } from "./ui/icon";
import { FormattedNumberInput } from "./ui/formatted-number-input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "~/components/ui/select";
import { SelectProps } from "@radix-ui/react-select";
import { cn } from "~/lib/utils";
import CreatableSelect from "react-select/creatable";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { Loader2, XIcon } from "lucide-react";
import { useRouteLoaderData } from "react-router";
import { loader as rootLoader } from "~/root";
import { Address } from "~/lib/db/schema";

export type ListOfErrors = Array<string | null | undefined> | null | undefined;

export const ErrorList = ({
  id,
  errors,
}: {
  id?: string;
  errors?: ListOfErrors;
}) => {
  const errorsToRender = errors?.filter(Boolean);
  if (!errorsToRender?.length) return null;
  return (
    <ul id={id} className="flex flex-col gap-0.5 -mb-1">
      {errorsToRender.map((e) => (
        <li key={e} className="text-xs font-semibold text-destructive">
          {e}
        </li>
      ))}
    </ul>
  );
};

export const Field = ({
  labelProps,
  inputProps,
  errors,
  className,
  helperText,
  type = "default",
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps: InputProps & {
    key?: string;
    ref?: React.RefObject<HTMLInputElement>;
  };
  errors?: ListOfErrors;
  className?: string;
  helperText?: string;
  size?: "default" | "lg";
  type?: "default" | "money";
}) => {
  const fallbackId = useId();

  // Guard against undefined inputProps
  if (!inputProps) {
    console.error("Field component received undefined inputProps");
    return null;
  }

  const id = inputProps.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;

  const { key, ref, ...inputPropsWithoutKey } = inputProps;

  // Check if this is a number input to use FormattedNumberInput
  // TODO: FormattedNumberInput component is ready but needs more testing
  // to ensure it works correctly with all existing forms and validation logic.
  // Enable by setting this to: inputProps.type === "number" && !inputProps.readOnly
  const isNumberInput = false;
  const step = inputProps.step;

  // Determine decimal places from step if provided
  const getDecimalPlaces = (step?: number | string) => {
    if (!step) return 2;
    const stepStr = String(step);
    const decimalIndex = stepStr.indexOf(".");
    if (decimalIndex === -1) return 0;
    return stepStr.length - decimalIndex - 1;
  };

  return (
    <fieldset className={cn("group relative flex flex-col gap-2", className)}>
      <Label htmlFor={id} {...labelProps} />
      {isNumberInput ? (
        <FormattedNumberInput
          key={key}
          ref={ref}
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          allowDecimals={step !== "1" && step !== 1}
          decimalPlaces={getDecimalPlaces(step)}
          {...inputPropsWithoutKey}
        />
      ) : (
        <Input
          key={key}
          ref={ref}
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          withCurrency={type === "money"}
          {...inputPropsWithoutKey}
        />
      )}
      {errorId ? (
        <ErrorList id={errorId} errors={errors} />
      ) : (
        helperText && (
          <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
            <Icon name="information-line" className="text-xs" />
            {helperText}
          </div>
        )
      )}
    </fieldset>
  );
};

export const TextareaField = ({
  labelProps,
  textareaProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  errors?: ListOfErrors;
  className?: string;
}) => {
  const fallbackId = useId();
  const id = textareaProps.id ?? textareaProps.name ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;
  return (
    <fieldset className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={id} {...labelProps} />
      <Textarea
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...textareaProps}
      />
      {errorId && <ErrorList id={errorId} errors={errors} />}
    </fieldset>
  );
};

export const CheckboxField = ({
  labelProps,
  buttonProps,
  errors,
  className,
}: {
  labelProps: JSX.IntrinsicElements["label"];
  buttonProps: CheckboxProps & {
    name: string;
    form: string;
    value?: string;
  };
  errors?: ListOfErrors;
  className?: string;
}) => {
  const { key, defaultChecked, ...checkboxProps } = buttonProps;
  const fallbackId = useId();
  const checkedValue = buttonProps.value ?? "on";
  const input = useInputControl({
    key,
    name: buttonProps.name,
    formId: buttonProps.form,
    initialValue: defaultChecked ? checkedValue : undefined,
  });
  const id = buttonProps.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;

  return (
    <fieldset className={cn("flex flex-col gap-1", className)}>
      <div className="flex gap-2">
        <Checkbox
          {...checkboxProps}
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          checked={input.value === checkedValue}
          onCheckedChange={(state) => {
            input.change(state.valueOf() ? checkedValue : "");
            buttonProps.onCheckedChange?.(state);
          }}
          onFocus={(event) => {
            input.focus();
            buttonProps.onFocus?.(event);
          }}
          onBlur={(event) => {
            input.blur();
            buttonProps.onBlur?.(event);
          }}
          type="button"
        />
        <label
          htmlFor={id}
          {...labelProps}
          className="self-center text-body-sm text-muted-foreground"
        />
      </div>
      {errorId && <ErrorList id={errorId} errors={errors} />}
    </fieldset>
  );
};

export const OTPField = ({
  labelProps,
  inputProps,
  errors,
  className,
  helperText,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps: Partial<OTPInputProps & { render: never }>;
  errors?: ListOfErrors;
  className?: string;
  helperText?: string;
}) => {
  const fallbackId = useId();
  const id = inputProps.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {labelProps && <Label htmlFor={id} {...labelProps} />}
      <InputOTP
        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
        maxLength={6}
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...inputProps}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>
      {errorId ? (
        <ErrorList id={errorId} errors={errors} />
      ) : (
        helperText && (
          <div className="flex items-center gap-0.5 px-2 text-xs text-muted-foreground">
            <Icon name="information-line" className="text-xs" />
            {helperText}
          </div>
        )
      )}
    </div>
  );
};

export const SelectField = ({
  labelProps,
  selectProps,
  selectTriggerProps,
  selectContentProps,
  selectPlaceholder,
  errors,
  className,
  helperText,
  options,
  noOptionsLinkNode,
  isLoading,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  selectProps: SelectProps & { key?: string; id?: string };
  selectTriggerProps?: React.ComponentProps<typeof SelectTrigger>;
  selectContentProps?: React.ComponentProps<typeof SelectContent>;
  selectPlaceholder?: string;
  errors?: ListOfErrors;
  className?: string;
  helperText?: string;
  options: Array<{ value: string; label: string }>;
  noOptionsLinkNode?: React.ReactNode;
  isLoading?: boolean;
}) => {
  const fallbackId = useId();
  const id = selectProps?.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;

  const { key, ...selectPropsWithoutKey } = selectProps;

  return (
    <fieldset
      className={cn(
        "group relative flex flex-col gap-2 p-px -mt-px w-full max-w-full min-w-0",
        className
      )}
    >
      <Label htmlFor={id} {...labelProps} />
      <Select
        key={key}
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...selectPropsWithoutKey}
      >
        <SelectTrigger
          className={cn(
            "relative flex items-center w-full max-w-full min-w-0 [&>span]:truncate",
            selectTriggerProps?.className
          )}
          noCaret={isLoading}
        >
          <SelectValue
            placeholder={selectPlaceholder || "Selecciona una opción"}
          />
          {isLoading && (
            <Loader2 className="z-50 absolute right-3 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </SelectTrigger>
        <SelectContent className={cn("w-full", selectContentProps?.className)}>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
            {noOptionsLinkNode && (
              <div className="relative group flex items-center justify-between hover:underline text-sm cursor-pointer">
                {noOptionsLinkNode}
                <Icon
                  name="arrow-right"
                  className="absolute right-3 transition-all -translate-x-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"
                />
              </div>
            )}
          </SelectGroup>
        </SelectContent>
      </Select>
      {errorId ? (
        <ErrorList id={errorId} errors={errors} />
      ) : (
        helperText && (
          <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
            <Icon name="information-line" className="text-xs" />
            {helperText}
          </div>
        )
      )}
    </fieldset>
  );
};

export const AddressField = ({
  label,
  id,
  defaultValue,
  inputClassName,
  onCoordinatesChange,
  placeholder = "Busca una dirección",
  errors,
  onInputChange,
}: {
  label?: string;
  id?: string;
  defaultValue?: string;
  inputClassName?: string;
  onCoordinatesChange?: (
    coordinates:
      | {
          longitude: number;
          latitude: number;
          address: string;
        }
      | null
      | string
  ) => void;
  placeholder?: string;
  errors?: ListOfErrors;
  onInputChange?: React.ChangeEventHandler<HTMLInputElement>;
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const lastInputValueRef = useRef(defaultValue);

  const data = useRouteLoaderData<typeof rootLoader>("root");
  const googleMapsApiKey = data?.ENV.GOOGLE_MAPS_API_KEY;
  const isTestMode = data?.ENV.IS_DEV === "true";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (onInputChange) {
      onInputChange(e);
    }

    if (!isSelecting && value !== lastInputValueRef.current) {
      lastInputValueRef.current = value;
      onCoordinatesChange?.(value);
    }
  };

  // Mock addresses for testing - no Google Maps API calls
  const handleTestModeSelection = (input: HTMLInputElement) => {
    const mockAddresses = [
      {
        address: "Test Address 123, Test City, Test State 12345",
        latitude: 19.4326,
        longitude: -99.1332,
      },
      {
        address: "Mock Street 456, Mock City, Mock State 67890",
        latitude: 20.6597,
        longitude: -103.3496,
      },
      {
        address: "Demo Avenue 789, Demo City, Demo State 54321",
        latitude: 25.6866,
        longitude: -100.3161,
      },
    ];

    // Simple mock selection - use first address if input contains "test" or "mock"
    const inputValue = input.value.toLowerCase();
    let selectedAddress = mockAddresses[0];

    if (inputValue.includes("mock")) {
      selectedAddress = mockAddresses[1];
    } else if (inputValue.includes("demo")) {
      selectedAddress = mockAddresses[2];
    }

    const coordinates = {
      latitude: selectedAddress.latitude,
      longitude: selectedAddress.longitude,
      address: selectedAddress.address,
    };

    onCoordinatesChange?.(coordinates);
    lastInputValueRef.current = selectedAddress.address;
    input.value = selectedAddress.address;
  };

  // Load Google Maps script first (only in non-test mode)
  useEffect(() => {
    if (isTestMode) {
      setIsLoaded(true);
      return;
    }

    if (!googleMapsApiKey) return;
    if (typeof window !== "undefined" && (window as any).google?.maps) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);

    document.head.appendChild(script);
    return () => {
      // Cleanup if component unmounts before script loads
      script.remove();
    };
  }, [googleMapsApiKey, isTestMode]);

  // Initialize Autocomplete only once after script is loaded (skip in test mode)
  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    if (isTestMode) {
      // In test mode, simulate autocomplete with mock data on blur
      const input = inputRef.current;
      const handleBlur = () => {
        if (input.value) {
          handleTestModeSelection(input);
        }
      };

      input.addEventListener("blur", handleBlur);
      return () => {
        input.removeEventListener("blur", handleBlur);
      };
    }

    const input = inputRef.current;
    const autocomplete = new (window as any).google.maps.places.Autocomplete(
      input,
      {
        fields: ["formatted_address", "geometry"],
      }
    );

    autocompleteRef.current = autocomplete;

    const placeChangedListener = (window as any).google.maps.event.addListener(
      autocomplete,
      "place_changed",
      () => {
        const place = autocomplete.getPlace();
        setIsSelecting(false);

        if (!place.geometry?.location) {
          onCoordinatesChange?.(input.value);
          lastInputValueRef.current = input.value;
          return;
        }

        const coordinates = {
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          address: place.formatted_address || "",
        };

        onCoordinatesChange?.(coordinates);
        lastInputValueRef.current = place.formatted_address || "";
      }
    );

    // Only clean up the place_changed listener on unmount
    return () => {
      if (autocompleteRef.current) {
        (window as any).google.maps.event.removeListener(placeChangedListener);
        autocompleteRef.current = null;
      }
    };
  }, [isLoaded, isTestMode]);

  return (
    <Field
      labelProps={{
        children: label,
      }}
      inputProps={{
        id,
        ref: inputRef,
        type: "text",
        placeholder,
        defaultValue,
        className: inputClassName,
        onChange: handleInputChange,
        onFocus: () => setIsSelecting(true),
        onBlur: () => {
          setTimeout(() => {
            setIsSelecting(false);
          }, 200);
        },
      }}
      errors={errors}
    />
  );
};

export const ComboboxField = ({
  labelProps,
  comboboxProps,
  comboboxPlaceholder = "Selecciona una opción",
  errors,
  className,
  helperText,
  options,
  selectClassName,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  comboboxProps: {
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    name?: string;
    id?: string;
    key?: string;
    defaultValue?: string;
  };
  comboboxPlaceholder?: string;
  errors?: ListOfErrors;
  className?: string;
  helperText?: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  selectClassName?: string;
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(
    comboboxProps?.defaultValue || ""
  );

  const fallbackId = useId();
  const id = comboboxProps?.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;

  const { key, value, onChange, disabled, name } = comboboxProps;

  const selectedOption = options.find((option) => option.value === value);
  const defaultOption = options.find(
    (option) => option.value === comboboxProps.defaultValue
  );

  return (
    <fieldset
      className={cn(
        "group relative flex flex-col gap-2 p-px -mt-px",
        className
      )}
    >
      <Label htmlFor={id} {...labelProps} />
      <CreatableSelect
        key={key}
        inputId={id}
        defaultValue={defaultOption}
        value={selectedOption}
        onChange={(newValue) => {
          if (onChange) {
            onChange(newValue?.value ?? "");
          } else {
            setSelectedValue(newValue?.value ?? "");
          }
        }}
        options={options}
        isDisabled={disabled}
        placeholder={comboboxPlaceholder}
        isClearable
        isSearchable
        classNames={{
          container: () => "relative",
          control: () =>
            cn(
              "!min-h-12 md:!min-h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors",
              "hover:border-input",
              "focus-within:outline-none focus-within:ring-1 focus-within:ring-ring",
              disabled && "cursor-not-allowed opacity-50"
            ),
          input: () => "text-foreground",
          valueContainer: () => "gap-1.5 cursor-text",
          singleValue: () => "text-foreground truncate",
          menu: () =>
            "mt-1 p-1 rounded-md border border bg-popover text-popover-foreground shadow-md text-sm",
          menuList: () => "p-0",
          option: ({ isFocused, isSelected }) =>
            cn(
              "relative flex cursor-default select-none items-center rounded-sm px-2 py-3 md:py-1.5 text-sm outline-none transition-colors",
              (isFocused || isSelected) && "bg-accent text-accent-foreground",
              "cursor-pointer"
            ),
          indicatorsContainer: () => "gap-1",
          dropdownIndicator: () =>
            "text-muted-foreground hover:text-foreground",
          indicatorSeparator: () => "bg-input",
          clearIndicator: () => "text-muted-foreground hover:text-foreground",
          noOptionsMessage: () =>
            "text-muted-foreground h-11 md:h-8 flex items-center justify-center",
          placeholder: () => "truncate",
        }}
        components={{
          DropdownIndicator: () => (
            <CaretSortIcon className="h-5 w-5 md:h-4 md:w-4 opacity-50" />
          ),
          IndicatorSeparator: () => null,
          ClearIndicator: (props) => (
            <XIcon
              onClick={(event) => {
                event.stopPropagation();
                props.clearValue();
              }}
              className="h-3 w-3 cursor-pointer opacity-50 hover:opacity-100 mr-0.5"
            />
          ),
        }}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        // @ts-expect-error – This works
        theme={(theme) => ({
          ...theme,
          colors: {},
          spacing: {},
        })}
        formatCreateLabel={(value) => `Crea el destino "${value}"`}
        className={selectClassName}
        menuShouldScrollIntoView={false}
        noOptionsMessage={() => "No hay opciones"}
      />
      <input type="hidden" name={name} value={selectedValue} />
      {errorId ? (
        <ErrorList id={errorId} errors={errors} />
      ) : helperText ? (
        <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
          <Icon name="information-line" className="text-xs" />
          {helperText}
        </div>
      ) : null}
    </fieldset>
  );
};

export const TextOrObjectAddressFields = ({
  address,
  entityAddress,
}: {
  address: Address | string;
  entityAddress: any;
}) => {
  if (typeof address === "string") {
    return <input type="hidden" name="address" value={address} />;
  }

  return (
    // If it's an object address AND we're editing
    entityAddress && typeof entityAddress !== "string" ? (
      <>
        <input type="hidden" name="address.id" value={entityAddress.id} />
        <input type="hidden" name="address.short" value={address.short} />
        <input type="hidden" name="address.long" value={address.long} />
        {address.coordinates && (
          <>
            <input
              type="hidden"
              name="address.coordinates.longitude"
              value={address.coordinates.x.toString()}
            />
            <input
              type="hidden"
              name="address.coordinates.latitude"
              value={address.coordinates.y.toString()}
            />
          </>
        )}
      </>
    ) : (
      // New object address
      <>
        <input type="hidden" name="address.short" value={address.short} />
        <input type="hidden" name="address.long" value={address.long} />
        {address.coordinates && (
          <>
            <input
              type="hidden"
              name="address.coordinates.longitude"
              value={address.coordinates.x.toString()}
            />
            <input
              type="hidden"
              name="address.coordinates.latitude"
              value={address.coordinates.y.toString()}
            />
          </>
        )}
      </>
    )
  );
};
